import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLazyGetStepInstanceByIdQuery, useLazyGetServiceRequestInstanceQuery } from "../../../../service/customersupport-service";
import { hideLoader, showLoader } from "../../../../stores/slices/loader-slice";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../../../utils/error-handler";
import styles from "./StepInstancePage.module.scss";
import StepInstanceForm from "./components/StepInstanceForm/StepInstanceForm";
import StepInstanceActions from "./components/StepInstanceActions/StepInstanceActions";
import { DynamicFieldItem } from "../../../../model/dynamic-forms";
import { StepInstanceResponse } from "../../../../model/workflows/step-instance";
import { ServiceRequestHistoryStepData } from "../../../../model/service-request/service-request-history-step-data";

type StepItem = {
	id: string;
	stepName: string;
	displayOrder: number;
	isCurrent: boolean;
	takenActionId: string | null;
	createdAt?: string;
};

const StepInstancePage = () => {
	const { StepInstanceId = "" } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [getStepInstance, { data: stepInstance, isLoading }] = useLazyGetStepInstanceByIdQuery();
	const [getHistoryStepInstance] = useLazyGetStepInstanceByIdQuery(); // Separate instance for history steps
	const [getServiceRequestInstance] = useLazyGetServiceRequestInstanceQuery();

	const [showCustomerDetails, setShowCustomerDetails] = useState(false);
	const [activeProgressItem, setActiveProgressItem] = useState<"customerDetails" | "currentStep" | string>("currentStep");
	// State management for additional form (editable)
	const [additionalFormFieldValues, setAdditionalFormFieldValues] = useState<DynamicFieldItem[]>([]);
	// State for history step instance
	const [historyStepInstance, setHistoryStepInstance] = useState<StepInstanceResponse | null>(null);
	const [loadingHistoryStep, setLoadingHistoryStep] = useState(false);
	// State for history steps data
	const [historyStepsData, setHistoryStepsData] = useState<ServiceRequestHistoryStepData[]>([]);
	// Cache for fetched history steps to avoid refetching
	const [historyStepsCache, setHistoryStepsCache] = useState<Map<string, StepInstanceResponse>>(new Map());
	// Combined and sorted steps for display
	const [allSteps, setAllSteps] = useState<StepItem[]>([]);

	useEffect(() => {
		if (StepInstanceId) {
			dispatch(showLoader());
			getStepInstance(StepInstanceId)
				.unwrap()
				.catch((error: any) => {
					toast.error(getErrorMessage(error.data) ?? "Error loading step instance");
				})
				.finally(() => {
					dispatch(hideLoader());
				});
		}
	}, [StepInstanceId, getStepInstance, dispatch]);

	// Fetch service request instance history steps data if flag is enabled
	useEffect(() => {
		if (stepInstance?.stepDefinition?.ableToSeeOtherStepInformation && stepInstance?.customerServiceRequestInstance?.id) {
			getServiceRequestInstance(stepInstance.customerServiceRequestInstance.id)
				.unwrap()
				.then(serviceRequestInstance => {
					if (serviceRequestInstance.historyStepsData) {
						setHistoryStepsData(serviceRequestInstance.historyStepsData);
					}
				})
				.catch((error: any) => {
					toast.error(getErrorMessage(error.data) ?? "Error loading history steps");
				});
		}
	}, [stepInstance?.stepDefinition?.ableToSeeOtherStepInformation, stepInstance?.customerServiceRequestInstance?.id, getServiceRequestInstance]);

	// Combine and sort all steps (history + current) for chronological display
	useEffect(() => {
		if (!stepInstance) return;

		const steps: StepItem[] = [];

		// Filter out current step from history steps
		const filteredHistorySteps = historyStepsData.filter((step: ServiceRequestHistoryStepData) => step.id !== StepInstanceId);

		// Add history steps if flag is enabled
		if (stepInstance.stepDefinition?.ableToSeeOtherStepInformation) {
			filteredHistorySteps.forEach((historyStep: ServiceRequestHistoryStepData) => {
				// Try to get displayOrder from cached instance
				const cachedInstance = historyStepsCache.get(historyStep.id);
				const displayOrder = cachedInstance?.stepDefinition?.displayOrder ?? 0;

				steps.push({
					id: historyStep.id,
					stepName: historyStep.stepName,
					displayOrder,
					isCurrent: false,
					takenActionId: cachedInstance?.takenActionId ?? null,
					createdAt: historyStep.createdAt,
				});
			});
		}

		// Always add current step
		// Get createdAt from stepInstance, or use current date as fallback
		const currentStepCreatedAt = stepInstance.createdAt ?? new Date().toISOString();

		steps.push({
			id: "currentStep",
			stepName: stepInstance.stepDefinition?.name ?? "Unknown Step",
			displayOrder: stepInstance.stepDefinition?.displayOrder ?? 0,
			isCurrent: true,
			takenActionId: stepInstance.takenActionId,
			createdAt: currentStepCreatedAt,
		});

		// Sort steps by createdAt (chronological order)
		steps.sort((a, b) => {
			if (a.createdAt && b.createdAt) {
				return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
			}
			// If one is missing createdAt, prioritize the one that has it
			if (!a.createdAt) return 1;
			if (!b.createdAt) return -1;
			return 0;
		});

		setAllSteps(steps);
	}, [stepInstance, historyStepsData, historyStepsCache, StepInstanceId]);

	if (isLoading) {
		return null;
	}

	if (!stepInstance) {
		return null;
	}

	console.log(stepInstance);

	// Handle history step click
	const handleHistoryStepClick = async (stepId: string) => {
		setShowCustomerDetails(false);
		setActiveProgressItem(stepId);
		setHistoryStepInstance(null);

		if (stepId === "currentStep") {
			setHistoryStepInstance(null);
			return;
		}

		// Check if history step is already cached
		const cachedStep = historyStepsCache.get(stepId);
		if (cachedStep) {
			setHistoryStepInstance(cachedStep);
			return;
		}

		try {
			setLoadingHistoryStep(true);
			dispatch(showLoader());
			const historyStep = await getHistoryStepInstance(stepId).unwrap();

			// Cache the fetched history step (this will trigger the useEffect to update allSteps with correct displayOrder)
			setHistoryStepsCache(prevCache => {
				const newCache = new Map(prevCache);
				newCache.set(stepId, historyStep);
				return newCache;
			});

			setHistoryStepInstance(historyStep);
		} catch (error: any) {
			toast.error(getErrorMessage(error.data) ?? "Error loading history step");
		} finally {
			setLoadingHistoryStep(false);
			dispatch(hideLoader());
		}
	};

	const isHistoryStepActive = activeProgressItem !== "customerDetails" && activeProgressItem !== "currentStep";
	const displayedStepInstance = isHistoryStepActive && historyStepInstance ? historyStepInstance : stepInstance;
	const displayedStepInstanceId = isHistoryStepActive ? activeProgressItem : StepInstanceId;

	return (
		<div className={styles.pageContainer}>
			<div className={styles.header}>
				<div className={styles.breadcrumb}>
					<span>
						{stepInstance.customerServiceRequestInstance?.serviceRequest.name} - {stepInstance.customerServiceRequestInstance?.serviceRequest.description}
					</span>
				</div>
				<div className={styles.headerInfo}>
					<div className={styles.reqId}>Req ID: {stepInstance.customerServiceRequestInstance?.id}</div>
					{/* <div className={styles.assignedBy}>Assigned By: Chijioke Adebayo</div> */}
				</div>
			</div>

			<div className={styles.progressTracker}>
				{stepInstance.stepDefinition?.ableToSeeCustomerDetails && (
					<div
						className={`${styles.detailsCustomerProvidedButton} ${activeProgressItem === "customerDetails" ? styles.active : ""}`}
						onClick={() => {
							setShowCustomerDetails(true);
							setActiveProgressItem("customerDetails");
						}}
					>
						<span>Details Customer Provided</span>
					</div>
				)}
				<div className={styles.workflowSteps}>
					{allSteps.map((step: StepItem) => {
						if (step.isCurrent) {
							return (
								<div
									key={step.id}
									className={`${styles.currentStep} ${step.takenActionId ? styles.completed : ""} ${activeProgressItem === "currentStep" ? styles.active : ""}`}
									onClick={() => {
										setShowCustomerDetails(false);
										setActiveProgressItem("currentStep");
										setHistoryStepInstance(null);
									}}
								>
									<span>
										Step {step.displayOrder}: {step.stepName}
									</span>
								</div>
							);
						} else {
							return (
								<div
									key={step.id}
									className={`${styles.currentStep} ${styles.completed} ${activeProgressItem === step.id ? styles.active : ""}`}
									onClick={() => handleHistoryStepClick(step.id)}
								>
									<span>
										{step.stepName}
										<span className={styles.checkmark}>✓</span>
									</span>
								</div>
							);
						}
					})}
				</div>
			</div>

			{showCustomerDetails ? (
				<div className={styles.content}>
					<div className={styles.titleSection}>
						<h2 className={styles.title}>Details Customer Provided for {stepInstance.customerServiceRequestInstance?.serviceRequest.name}</h2>
					</div>

					<div className={styles.formCard}>
						<StepInstanceForm
							serviceRequestInstance={stepInstance.customerServiceRequestInstance ?? undefined}
							serviceRequestType={stepInstance.customerServiceRequestInstance?.serviceRequest ?? undefined}
							isReadOnly={true}
						/>
					</div>
				</div>
			) : (
				<div className={styles.content}>
					<div className={styles.titleSection}>
						<h2 className={styles.title}>{displayedStepInstance.stepDefinition?.name}</h2>
					</div>

					<div className={styles.formCard}>
						{isHistoryStepActive ? (
							<>
								{loadingHistoryStep ? (
									<div>Loading history step...</div>
								) : (
									<>
										{displayedStepInstance.additionalServiceRequestInstance && (
											<StepInstanceForm
												serviceRequestInstance={displayedStepInstance.additionalServiceRequestInstance}
												serviceRequestType={displayedStepInstance.stepDefinition?.additionalServiceRequest ?? undefined}
												isReadOnly={true}
											/>
										)}
										{displayedStepInstance.takenActionId && displayedStepInstance.takenAction && (
											<div className={styles.takenActionContainer}>
												<div className={styles.takenActionLabel}>Taken Action:</div>
												<div className={styles.takenActionItem}>
													<span className={styles.checkmark}>✓</span>
													<span>{displayedStepInstance.takenAction.name}</span>
												</div>
											</div>
										)}
										<StepInstanceActions
											actions={displayedStepInstance.stepDefinition?.actions ?? []}
											stepInstanceId={displayedStepInstanceId}
											additionalServiceRequest={displayedStepInstance.stepDefinition?.additionalServiceRequest ?? undefined}
											formFieldValues={[]}
											takenActionId={displayedStepInstance.takenActionId}
											takenAction={displayedStepInstance.takenAction}
										/>
									</>
								)}
							</>
						) : (
							<>
								<StepInstanceForm
									serviceRequestInstance={stepInstance.additionalServiceRequestInstance ?? undefined}
									serviceRequestType={stepInstance.stepDefinition?.additionalServiceRequest ?? undefined}
									onFormValuesChange={setAdditionalFormFieldValues}
									isReadOnly={!!stepInstance.takenActionId}
									initialFormValues={additionalFormFieldValues.length > 0 ? additionalFormFieldValues : undefined}
								/>
								{stepInstance.takenActionId && stepInstance.takenAction && (
									<div className={styles.takenActionContainer}>
										<div className={styles.takenActionLabel}>Taken Action:</div>
										<div className={styles.takenActionItem}>
											<span className={styles.checkmark}>✓</span>
											<span>{stepInstance.takenAction.name}</span>
										</div>
									</div>
								)}
								<StepInstanceActions
									actions={stepInstance.stepDefinition?.actions ?? []}
									stepInstanceId={StepInstanceId}
									additionalServiceRequest={stepInstance.stepDefinition?.additionalServiceRequest ?? undefined}
									formFieldValues={additionalFormFieldValues}
									takenActionId={stepInstance.takenActionId}
									takenAction={stepInstance.takenAction}
								/>
							</>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default StepInstancePage;
